import { useNavigate } from '@tanstack/react-router';
import { Copy } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge, Button, Dialog, Field, Input, Select } from '../../../components/ui';
import { useT } from '../../../i18n';
import { useAutomationTeams } from '../api';
import { useCopyProject, useCreateProject } from '../hooks';
import {
  emptyProjectDraft,
  newProjectInput,
  ProjectForm,
  projectDraftValid,
  Segmented,
  useFailToast,
  type ProjectDraft,
} from './shared';

type Mode = 'new' | 'copy';

/**
 * A new project, or a copy of another team's project with its settings and
 * catalogue (and, if wanted, its test data). Admins only.
 */
export const AddProjectDialog = ({
  teamId,
  onOpenChange,
}: {
  teamId: string;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useT();
  const navigate = useNavigate();
  const failToast = useFailToast();
  const create = useCreateProject(teamId);
  const copy = useCopyProject(teamId);
  const teams = useAutomationTeams();
  const [mode, setMode] = useState<Mode>('new');
  const [draft, setDraft] = useState<ProjectDraft>(emptyProjectDraft);
  const [sourceId, setSourceId] = useState('');
  const [copyName, setCopyName] = useState('');
  const [withTestData, setWithTestData] = useState(true);

  // only another team's project can be copied: a copy within the team would clash with it
  const foreign = (teams.data ?? [])
    .filter((team) => team.id !== teamId)
    .flatMap((team) => team.projects.map((project) => ({ team, project })));
  const source = foreign.find((entry) => entry.project.id === sourceId)?.project;

  const openProject = (projectId: string) => {
    onOpenChange(false);
    void navigate({
      to: '/automation/teams/$teamId/projects/$projectId',
      params: { teamId, projectId },
      search: {},
    });
  };

  const submitNew = () =>
    create.mutate(newProjectInput(draft), {
      onSuccess: (project) => {
        toast.success(t('automation.project.addProject.added', { name: project.name }));
        openProject(project.id);
      },
      onError: failToast,
    });

  const submitCopy = () =>
    copy.mutate(
      {
        sourceProjectId: sourceId,
        withTestData,
        ...(copyName.trim() ? { name: copyName.trim() } : {}),
      },
      {
        onSuccess: (project) => {
          toast.success(
            t('automation.project.addProject.copied', {
              name: project.name,
              classes: project.copied.classes,
              suites: project.copied.suites,
            }),
          );
          openProject(project.id);
        },
        // usually the name is taken in this team
        onError: failToast,
      },
    );

  const footer =
    mode === 'new' ? (
      <>
        <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
        <Button
          variant="primary"
          disabled={!projectDraftValid(draft)}
          loading={create.isPending}
          onClick={submitNew}
        >
          {t('automation.project.addProject.title')}
        </Button>
      </>
    ) : foreign.length ? (
      <>
        <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
        <Button
          variant="primary"
          disabled={!sourceId}
          loading={copy.isPending}
          onClick={submitCopy}
        >
          <Copy className="size-3.5" />
          {t('automation.project.addProject.copy')}
        </Button>
      </>
    ) : undefined;

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      title={t('automation.project.addProject.title')}
      size="lg"
      footer={footer}
    >
      <div className="flex flex-col gap-4">
        <Segmented
          label={t('automation.project.addProject.mode')}
          value={mode}
          onChange={setMode}
          options={[
            { value: 'new', label: t('automation.project.addProject.modeNew') },
            { value: 'copy', label: t('automation.project.addProject.modeCopy') },
          ]}
        />

        {mode === 'new' ? (
          <ProjectForm draft={draft} onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}>
            <p className="text-[12px] text-ink-subtle">{t('automation.project.addProject.hint')}</p>
          </ProjectForm>
        ) : !foreign.length ? (
          <p className="text-[13px] text-ink-muted">
            {t('automation.project.addProject.noForeign')}
          </p>
        ) : (
          <>
            <Field
              label={t('automation.project.addProject.source')}
              hint={t('automation.project.addProject.sourceHint')}
              required
            >
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={sourceId}
                  onChange={(event) => {
                    setSourceId(event.target.value);
                    setCopyName('');
                  }}
                >
                  <option value="">{t('automation.project.addProject.sourcePlaceholder')}</option>
                  {foreign.map(({ team, project }) => (
                    <option key={project.id} value={project.id}>
                      {team.name} → {project.name} ·{' '}
                      {t('automation.projectTests', { count: project.testCount })}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            {source ? (
              <div className="rounded-lg border border-border p-3 text-[13px]">
                <div className="flex flex-wrap items-center gap-3 text-ink-muted">
                  <span>
                    {t('automation.project.field.gitlabProjectId')}{' '}
                    <b className="font-mono text-ink">{source.gitlabProjectId}</b>
                  </span>
                  <span>{t('automation.projectTests', { count: source.testCount })}</span>
                  <Badge>{source.testType}</Badge>
                  {source.sourceProject ? (
                    <span className="text-ink-subtle">{t('automation.project.repoConnected')}</span>
                  ) : null}
                </div>
                {source.description ? (
                  <p className="mt-2 text-ink-muted">{source.description}</p>
                ) : null}
              </div>
            ) : null}

            <Field
              label={t('automation.project.addProject.copyName')}
              hint={t('automation.project.addProject.copyNameHint')}
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={copyName}
                  placeholder={
                    source?.name ?? t('automation.project.addProject.copyNamePlaceholder')
                  }
                  onChange={(event) => setCopyName(event.target.value)}
                />
              )}
            </Field>

            <label className="flex items-center gap-2 text-[13px] text-ink">
              <input
                type="checkbox"
                checked={withTestData}
                onChange={(event) => setWithTestData(event.target.checked)}
                className="size-4 rounded border-border"
              />
              {t('automation.project.addProject.withTestData')}
            </label>
          </>
        )}
      </div>
    </Dialog>
  );
};
